import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { AuthFlowResponse, AuthFlowResponseType } from '../../services/auth/authFlowSchema';
import { LoadPage } from '../layout';
import { Routes } from '../../routes';
import { Navigate } from 'react-router-dom';
import { ErrorPage, WidgetErrorPage } from '../../pages/error';
import { useRoutingService } from '../../services/routing/routingHooks';
import { Error } from '../../core/error';
import { useStore } from '../../store/storeHooks';
import { notifyError } from '../../services/notifications';
import { useKernel } from '../../kernel/kernelHooks';
import { ClientMode } from '../../kernel/kernelSchema';

type Props = {
  response?: AuthFlowResponse | null;
  error?: Error | null;
}

export const AuthFlowResponseInterpreter = observer(({
  response,
  error
}: Props) => {

  const store = useStore();
  const kernel = useKernel();
  const { notificationService } = store;

  const responseType: AuthFlowResponseType | null = response?.responseType ?? null;
  const responseError: Error | null = response?.error ?? null;

  // Note: redirectPath is stored for potential future use but not currently needed
  // In history v5, state is passed as second argument to push/replace
  const _redirectPath = response?.redirectPath;

  const routingService = useRoutingService();

  if (error) {
    switch (kernel.clientMode) {
      case ClientMode.Widget:
        return (
          <WidgetErrorPage error={error} />
        );
      default:
        return (
          <ErrorPage code={error.code} message={error.message} />
        );
    }
  }

  useEffect(() => {
    if (responseError)
      notifyError(notificationService, responseError.message);
  }, [responseError, notificationService]);

  // Navigation is handled via the Navigate component in the render below.
  // This ensures proper React Router v7 integration without duplicate navigation calls.

  switch (responseType) {
    case null:
    case AuthFlowResponseType.AwaitRedirect:
      return (
        <LoadPage />
      );

    case AuthFlowResponseType.RedirectToLoginPage:
      return (
        <Navigate to={Routes.login()} replace />
      );

    case AuthFlowResponseType.RedirectToOnboardPage:
      return (
        <Navigate to={Routes.onboard()} replace />
      );

    case AuthFlowResponseType.RedirectToExternalLoginWidget:
      return (
        <Navigate to={Routes.externalLoginWidget()} replace />
      );

    case AuthFlowResponseType.RedirectToProxyLoginWidget:
      return (
        <Navigate to={Routes.proxyLoginWidget()} replace />
      );

    case AuthFlowResponseType.RedirectToLastPrivateRoute: {
      const redirPath = routingService.storage.lastPrivatePath;
      return (
        <Navigate to={redirPath ?? Routes.defaultRoute()} replace />
      );
    }

    case AuthFlowResponseType.RedirectToLastWidgetRoute: {
      const redirUrl = routingService.lastWidgetRoute?.relativeUrl;
      if (!redirUrl) {
        const err = new Error('InternalError', `Invalid redirect state.`);
        return (
          <WidgetErrorPage error={err} />
        );
      }

      return (
        <Navigate to={redirUrl} replace />
      );
    }

    case AuthFlowResponseType.RedirectToSessionWidgetRoute: {
      const redirUrl = routingService.sessionWidgetRoute?.relativeUrl;
      if (!redirUrl) {
        const err = new Error('InternalError', `Invalid redirect state.`);
        return (
          <WidgetErrorPage error={err} />
        );
      }

      return (
        <Navigate to={redirUrl} replace />
      );
    }
  }

  return null;
});
